
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Settings, Loader2 } from 'lucide-react';
import { setBikeSpeedLimit } from '@/pages/admin/bikes/services/movementControlApi';
import { toast } from '@/components/ui/sonner';
import { BikeDetails } from '@/hooks/useBikeDetails';
import { supabase } from '@/integrations/supabase/client';

interface SpeedLimitQuickActionProps {
  bikeDetails: BikeDetails | null;
  onUpdate?: () => void;
}

export const SpeedLimitQuickAction: React.FC<SpeedLimitQuickActionProps> = ({
  bikeDetails,
  onUpdate
}) => {
  const [isLoading, setIsLoading] = useState(false);

  console.log('SpeedLimitQuickAction - bikeDetails:', bikeDetails);

  // Если нет ID велосипеда, показываем неактивную кнопку
  if (!bikeDetails?.id) {
    console.log('SpeedLimitQuickAction - нет ID велосипеда');
    return (
      <Button
        variant="outline"
        className="flex flex-col h-20 gap-1 p-2 col-span-2 text-xs opacity-50"
        disabled
      >
        <Settings className="h-5 w-5" />
        <span className="text-xs font-medium">
          Данные велосипеда загружаются...
        </span>
      </Button>
    );
  }

  const hasSpeedLimit = bikeDetails.speed_limit === 25;
  const isBlocked = bikeDetails.movement_blocked;

  console.log('SpeedLimitQuickAction - hasSpeedLimit:', hasSpeedLimit, 'isBlocked:', isBlocked);

  // Функция для проверки актуального статуса блокировки через базу данных
  const checkCurrentBlockStatus = async (): Promise<boolean> => {
    try {
      console.log('Проверяем актуальный статус блокировки для велосипеда:', bikeDetails.id);
      
      const { data, error } = await supabase
        .from('bikes')
        .select('movement_blocked')
        .eq('id', bikeDetails.id)
        .single();

      if (error) {
        console.error('Ошибка при проверке статуса блокировки:', error);
        throw error;
      }

      const currentBlockStatus = data?.movement_blocked || false;
      console.log('Актуальный статус блокировки из БД:', currentBlockStatus);
      
      return currentBlockStatus;
    } catch (error) {
      console.error('Ошибка при проверке статуса блокировки:', error);
      // В случае ошибки возвращаем локальный статус как fallback
      return isBlocked;
    }
  };

  const handleSpeedLimitToggle = async () => {
    try {
      // Курьер может только устанавливать ограничение, не снимать
      if (hasSpeedLimit) {
        toast.error('Снятие ограничения недоступно', {
          description: 'Только администратор может снять ограничение скорости. Обратитесь в службу поддержки.'
        });
        return;
      }

      // ПРОВЕРКА: Проверяем актуальный статус блокировки перед установкой ограничения
      console.log('Проверяем актуальный статус блокировки перед установкой ограничения...');
      
      const currentlyBlocked = await checkCurrentBlockStatus();
      
      if (currentlyBlocked) {
        toast.error('Невозможно установить ограничение скорости', {
          description: 'Велосипед заблокирован администратором. Для изменения ограничений обратитесь в службу поддержки.'
        });
        return;
      }
      
      console.log('Велосипед не заблокирован - разрешаем установку ограничения');

      setIsLoading(true);
      
      // Курьер может только устанавливать ограничение 25 км/ч
      const result = await setBikeSpeedLimit(bikeDetails.id, 25);
      
      if (result.success) {
        toast.success('Установлено ограничение скорости 25 км/ч');
        
        // Обновляем данные через колбэк
        if (onUpdate) {
          setTimeout(() => {
            onUpdate();
          }, 1000);
        }
      } else {
        toast.error(`Ошибка: ${result.error}`);
      }
    } catch (error) {
      console.error('Ошибка при установке ограничения скорости:', error);
      toast.error('Произошла ошибка при установке ограничения скорости');
    } finally {
      setIsLoading(false);
    }
  };

  // Определяем состояние кнопки
  const getButtonState = () => {
    if (hasSpeedLimit) {
      // Есть ограничение - курьер не может его снять
      return {
        variant: "outline" as const,
        className: "flex flex-col h-20 gap-1 p-2 col-span-2 text-xs bg-green-500 text-white opacity-75",
        disabled: true,
        text: "ОГРАНИЧЕНИЕ УСТАНОВЛЕНО",
        subtext: "Снятие доступно только администратору",
        icon: <Settings className="h-5 w-5 text-white" />
      };
    }
    
    if (isBlocked) {
      // Велосипед заблокирован - никаких действий не разрешаем
      return {
        variant: "outline" as const,
        className: "flex flex-col h-20 gap-1 p-2 col-span-2 text-xs bg-gray-400 text-white opacity-75",
        disabled: true,
        text: "ОГРАНИЧЕНИЕ НЕ УСТАНОВЛЕНО",
        subtext: "Заблокировано администратором",
        icon: <Settings className="h-5 w-5 text-white" />
      };
    }
    
    // Нет ограничения и велосипед не заблокирован - можно установить
    return {
      variant: "outline" as const,
      className: "flex flex-col h-20 gap-1 p-2 col-span-2 text-xs bg-orange-500 hover:bg-orange-600 text-white border-orange-600",
      disabled: false,
      text: "ОГРАНИЧИТЬ 25 КМ/Ч",
      subtext: null,
      icon: <Settings className="h-5 w-5 text-white" />
    };
  };

  const buttonState = getButtonState();

  return (
    <Button
      variant={buttonState.variant}
      className={buttonState.className}
      onClick={handleSpeedLimitToggle}
      disabled={isLoading || buttonState.disabled}
    >
      {isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-white" />
      ) : (
        buttonState.icon
      )}
      <div className="flex flex-col items-center">
        <span className="text-xs font-medium">
          {isLoading ? 'Установка...' : buttonState.text}
        </span>
        {buttonState.subtext && (
          <span className="text-xs opacity-80">
            {buttonState.subtext}
          </span>
        )}
      </div>
    </Button>
  );
};
